import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, Ban, DollarSign, Edit, PackageCheck, Trash2 } from 'lucide-react';
import AttachmentsListView from '../../components/AttachmentsListView';
import VoidConfirmationModal from '../../components/VoidConfirmationModal';
import TransactionOverlay from '../../components/transactions/TransactionOverlay';
import {
  PreviewActionFooter,
  PreviewCollapsibleLineItems,
  PreviewFinancialHero,
  PreviewLinkedList,
  PreviewMetaGrid,
  PreviewMetaItem,
  PreviewSectionHeading,
  PreviewSkeleton,
  previewActionClasses,
} from '../../components/transactions/TransactionPreviewShared';
import { deleteBill, getBillById, receiveBill, voidBill } from '../../utils/billApi';
import { getBillPayments } from '../../utils/billPaymentApi';
import type { Bill, BillStatus } from '../../types/bill';
import type { BillPayment } from '../../types/billPayment';
import type { Attachment } from '../../types/attachment';
import { getBillAttachments } from '../../utils/itemAttachmentApi';
import {
  canDeleteDraftBill,
  canEditBillTransaction,
  canPayBill,
  canReceiveBill,
  canVoidBill,
  isBillPaymentVoided,
} from '../../utils/purchaseTransactionPermissions';
import { toast } from '../../utils/toast';
import { billVoidCopy } from '../../utils/voidConfirmationCopy';
import { useInventoryTransactionsHub } from './InventoryTransactionsHubContext';
import {
  parseInventoryHubLocationState,
  billEditFromPreviewState,
  payBillFromPreviewState,
} from './inventoryHubNavigation';

const formatLabel = (value: string) =>
  value
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString();

const getStatusColor = (status: BillStatus) => {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700';
    case 'OPEN':
      return 'bg-blue-100 text-blue-800';
    case 'PARTIAL':
      return 'bg-yellow-100 text-yellow-800';
    case 'PAID':
      return 'bg-green-100 text-green-800';
    case 'VOIDED':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const vendorName = (bill: Bill) =>
  bill.vendor_snapshot?.name || bill.vendor?.display_name || '—';

const billTitle = (bill: Bill) => bill.vendor_bill_number ?? bill.bill_number;

type BillPaymentRow = {
  paymentId: number;
  paymentNumber: string;
  txnDate: string;
  paymentMethod: BillPayment['payment_method'];
  amountApplied: string;
};

const billHasPayments = (bill: Pick<Bill, 'status'>) =>
  bill.status === 'PAID' || bill.status === 'PARTIAL';

const extractBillPayments = (billId: number, payments: BillPayment[]): BillPaymentRow[] =>
  payments
    .filter((payment) => !isBillPaymentVoided(payment))
    .flatMap((payment) =>
      payment.allocations
        .filter((alloc) => alloc.bill_id === billId)
        .map((alloc) => ({
          paymentId: payment.id,
          paymentNumber: payment.payment_number,
          txnDate: payment.txn_date,
          paymentMethod: payment.payment_method,
          amountApplied: alloc.amount_applied,
        }))
    )
    .sort((a, b) => a.txnDate.localeCompare(b.txnDate) || a.paymentId - b.paymentId);

const InventoryBillPreview = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refetchList } = useInventoryTransactionsHub();
  const listSearch = location.search.replace(/^\?/, '');
  const transactionId = parseInventoryHubLocationState(location.state)?.transactionId;

  const billId = id != null ? Number(id) : Number.NaN;

  const [bill, setBill] = useState<Bill | null>(null);
  const [billPayments, setBillPayments] = useState<BillPaymentRow[]>([]);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidError, setVoidError] = useState<string | null>(null);
  const [voiding, setVoiding] = useState(false);

  const close = useCallback(() => {
    navigate({ pathname: '/inventory-transactions', search: listSearch }, { preventScrollReset: true });
  }, [listSearch, navigate]);

  const loadBill = useCallback(async () => {
    if (!id || Number.isNaN(billId)) {
      setError('Invalid bill ID');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const [data, billAttachments] = await Promise.all([
        getBillById(billId),
        getBillAttachments(billId).catch(() => []),
      ]);
      let paymentsForBill: BillPaymentRow[] = [];
      if (billHasPayments(data)) {
        const vendorPayments = await getBillPayments({ vendor_id: data.vendor_id }).catch(() => []);
        paymentsForBill = extractBillPayments(billId, vendorPayments);
      }
      setBill(data);
      setBillPayments(paymentsForBill);
      setAttachments(billAttachments);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load bill');
      setBill(null);
      setBillPayments([]);
      setAttachments([]);
    } finally {
      setLoading(false);
    }
  }, [billId, id]);

  useEffect(() => {
    void loadBill();
  }, [loadBill]);

  const openEdit = () => {
    if (!bill || !canEditBillTransaction(bill)) return;
    navigate(
      { pathname: `/inventory-transactions/bill/${bill.id}`, search: listSearch },
      { state: billEditFromPreviewState(bill.id, transactionId), preventScrollReset: true }
    );
  };

  const openPayment = (paymentId: number) => {
    const returnTo = `/inventory-transactions/view/bill/${bill?.id}${
      listSearch ? `?${listSearch}` : ''
    }`;
    navigate(
      {
        pathname: `/purchase-transactions/view/payment/${paymentId}`,
        search: listSearch,
      },
      {
        preventScrollReset: true,
        state: { returnTo, transactionId },
      }
    );
  };

  const handleReceive = async () => {
    if (!bill || !canReceiveBill(bill)) return;
    if (
      !window.confirm(
        `Receive bill ${bill.bill_number} into inventory? This will affect stock and GL.`
      )
    ) {
      return;
    }

    try {
      setActionLoading(true);
      await receiveBill(bill.id);
      refetchList();
      const updated = await getBillById(bill.id);
      setBill(updated);
      toast.success(`Bill ${bill.bill_number} received.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to receive bill');
    } finally {
      setActionLoading(false);
    }
  };

  const openVoidModal = () => {
    setVoidError(null);
    setShowVoidModal(true);
  };

  const cancelVoidModal = () => {
    setShowVoidModal(false);
    setVoidError(null);
  };

  const confirmVoid = async () => {
    if (!bill || !canVoidBill(bill)) return;

    try {
      setVoiding(true);
      setVoidError(null);
      await voidBill(bill.id);
      refetchList();
      const updated = await getBillById(bill.id);
      setBill(updated);
      setShowVoidModal(false);
      toast.success(`Bill ${bill.bill_number} voided.`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to void bill';
      setVoidError(message);
      toast.error(message);
    } finally {
      setVoiding(false);
    }
  };

  const handlePayBill = () => {
    if (!bill || !canPayBill(bill.status)) return;
    navigate(
      { pathname: '/inventory-transactions/payment/pay', search: listSearch },
      {
        state: payBillFromPreviewState(bill.id, bill.vendor_id, bill.id, transactionId),
        preventScrollReset: true,
      }
    );
  };

  const handleDelete = async () => {
    if (!bill || !canDeleteDraftBill(bill)) return;
    if (!window.confirm(`Delete draft ${bill.bill_number}? This cannot be undone.`)) {
      return;
    }

    try {
      setActionLoading(true);
      await deleteBill(bill.id);
      refetchList();
      close();
      toast.success(`Bill ${bill.bill_number} deleted.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete bill');
    } finally {
      setActionLoading(false);
    }
  };

  const lines = bill?.lines ?? [];
  const totalPaidOnBill = billPayments.reduce(
    (sum, row) => sum + parseFloat(row.amountApplied),
    0
  );
  const billTotal = bill ? parseFloat(bill.total_amount) : 0;
  const billBalance = bill ? parseFloat(bill.balance) : 0;
  const paidPercent =
    billTotal > 0 ? Math.min(100, ((billTotal - billBalance) / billTotal) * 100) : 0;

  const actionFooter =
    bill && !loading && !error ? (
      <PreviewActionFooter>
        {canEditBillTransaction(bill) && (
          <button
            type="button"
            onClick={openEdit}
            disabled={actionLoading}
            className={previewActionClasses.primary}
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
        )}
        {canReceiveBill(bill) && (
          <button
            type="button"
            onClick={handleReceive}
            disabled={actionLoading}
            className={previewActionClasses.success}
          >
            <PackageCheck className="h-4 w-4" />
            Receive
          </button>
        )}
        {canPayBill(bill.status) && (
          <button
            type="button"
            onClick={handlePayBill}
            disabled={actionLoading}
            className={previewActionClasses.secondary}
          >
            <DollarSign className="h-4 w-4" />
            Pay bill
          </button>
        )}
        {canVoidBill(bill) && (
          <button
            type="button"
            onClick={openVoidModal}
            disabled={actionLoading || voiding}
            className={previewActionClasses.danger}
          >
            <Ban className="h-4 w-4" />
            Void
          </button>
        )}
        {canDeleteDraftBill(bill) && (
          <button
            type="button"
            onClick={handleDelete}
            disabled={actionLoading}
            className={previewActionClasses.dangerStrong}
          >
            <Trash2 className="h-4 w-4" />
            Delete draft
          </button>
        )}
        <button type="button" onClick={close} className={previewActionClasses.close}>
          Close
        </button>
      </PreviewActionFooter>
    ) : undefined;

  return (
    <>
    <TransactionOverlay
      variant="preview"
      titleMono
      title={bill ? billTitle(bill) : 'Bill'}
      subtitle={bill ? `${vendorName(bill)} · ${formatDate(bill.txn_date)}` : 'Loading…'}
      footer={actionFooter}
      onClose={close}
    >
      {loading ? (
        <PreviewSkeleton />
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm flex gap-2">
          <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      ) : bill ? (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(bill.status)}`}
            >
              {formatLabel(bill.status)}
            </span>
            <span
              className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                bill.received_into_inventory
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-600'
              }`}
            >
              {bill.received_into_inventory ? 'Received' : 'Not received'}
            </span>
          </div>

          <PreviewFinancialHero
            amounts={[
              { label: 'Total', amount: bill.total_amount },
              {
                label: billBalance > 0 ? 'Balance due' : 'Balance',
                amount: bill.balance,
                tone: billBalance > 0 ? 'owed' : 'paid',
              },
            ]}
            progressPercent={bill.status === 'PARTIAL' ? paidPercent : undefined}
          />

          <PreviewMetaGrid>
            <PreviewMetaItem label="Vendor">
              <span className="font-medium">{vendorName(bill)}</span>
            </PreviewMetaItem>
            <PreviewMetaItem label="Due">{formatDate(bill.due_date)}</PreviewMetaItem>
            {bill.vendor_bill_number ? (
              <PreviewMetaItem label="Our bill no.">{bill.bill_number}</PreviewMetaItem>
            ) : null}
          </PreviewMetaGrid>

          {billHasPayments(bill) && (
            <div>
              <PreviewSectionHeading>Payments applied</PreviewSectionHeading>
              {billPayments.length > 0 ? (
                <PreviewLinkedList
                  rows={billPayments.map((row) => ({
                    id: row.paymentId,
                    title: row.paymentNumber,
                    subtitle: `${formatDate(row.txnDate)} · ${formatLabel(row.paymentMethod)}`,
                    amount: row.amountApplied,
                    onClick: () => openPayment(row.paymentId),
                  }))}
                  footerLabel="Total paid"
                  footerAmount={String(totalPaidOnBill)}
                />
              ) : (
                <p className="text-xs text-gray-500 italic">Payment history unavailable.</p>
              )}
            </div>
          )}

          {lines.length > 0 && (
            <PreviewCollapsibleLineItems
              lines={lines.map((line) => ({
                id: line.id,
                name: line.item_snapshot?.name ?? line.description ?? 'Line',
                sku: line.item_snapshot?.sku,
                qty: line.qty,
                unitPrice: line.unit_price,
                lineTotal: line.line_total,
              }))}
            />
          )}

          {bill.memo && (
            <p className="text-sm text-gray-600">
              <span className="font-medium text-gray-700">Memo:</span> {bill.memo}
            </p>
          )}

          {attachments.length > 0 && <AttachmentsListView attachments={attachments} />}
        </div>
      ) : null}
    </TransactionOverlay>

    {bill && (
      <VoidConfirmationModal
        isOpen={showVoidModal}
        {...billVoidCopy(bill.bill_number)}
        isSubmitting={voiding}
        error={voidError}
        onCancel={cancelVoidModal}
        onConfirm={confirmVoid}
      />
    )}
    </>
  );
};

export default InventoryBillPreview;
